import { FCAdditionalProps, ID } from "@datassential/platform-ui-lib";
import { IOption } from "@models";
import ScatterChart from "@components/ScatterChart/ScatterChart";
import { useMemo } from "react";
import VersusScatterChartSelect from "@vms/VersusScatterChart/VersusScaterChartSelect";
import cn from "classnames";

export type VersusScatterChartItem<T> = {
    id: ID;
    color: string;
    values: Map<T, number | null>;
    printValues: Map<T, number>;
};

type Props<T> = FCAdditionalProps & {
    data: VersusScatterChartItem<T>[];
    accentItemsIds?: ID[];
    options: IOption<T>[];
    xOptionId: T;
    setXOptionId: (id: T) => void;
    yOptionId: T;
    setYOptionId: (id: T) => void;
    setActiveItemId?: (id: ID) => void;
    id: string;
}

const VersusScatterChart = <T = undefined>({
    data,
    accentItemsIds = [],
    options,
    xOptionId,
    setXOptionId,
    yOptionId,
    setYOptionId,
    setActiveItemId,
    className,
    id,
}: Props<T>) => {
    const series = useMemo(
        () => {
            const mappedData = data
                .map(item => ({
                    id: item.id,
                    color: item.color,
                    x: item.values.get(xOptionId),
                    y: item.values.get(yOptionId),
                }))
                .filter(item => item.x !== null && item.y !== null);

            if (accentItemsIds.length > 0) {
                const accentItems = mappedData.filter(i => accentItemsIds.includes(i.id));
                const restItems = mappedData.filter(i => !accentItemsIds.includes(i.id));

                return [
                    { data: accentItems, opacity: 1, },
                    { data: restItems, opacity: 0.2, },
                ];
            }

            return [{
                data: mappedData,
            }]
        },
        [data, accentItemsIds, xOptionId, yOptionId]
    );

    return (
        <div className={cn("VersusScatterChart", className)}>
            <div className="VersusScatterChart__inner">
                <ScatterChart
                    series={series}
                    height={500}
                    onPointClick={setActiveItemId}
                    id={id}
                />
                <VersusScatterChartSelect
                    options={options}
                    value={xOptionId}
                    otherValue={yOptionId}
                    setValue={setXOptionId}
                    className="bottom-right"
                />
                <VersusScatterChartSelect
                    options={options}
                    value={yOptionId}
                    otherValue={xOptionId}
                    setValue={setYOptionId}
                    className="top-left"
                />
            </div>
        </div>
    );
};

export default VersusScatterChart;